import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input, Checkbox, Button, Spin, message, Tag, Modal } from 'antd';
import { LeftOutlined, SearchOutlined, CloseOutlined, CheckCircleFilled } from '@ant-design/icons';
import api from '../api';

function parsePresetAssetId(location) {
  const fromState = location.state?.assetId;
  const fromQuery = new URLSearchParams(location.search).get('assetId');
  const raw = fromState != null && fromState !== '' ? fromState : fromQuery;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function ApplyBorrow() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetAssetId = useMemo(() => parsePresetAssetId(location), [location]);

  const [assets, setAssets] = useState([]);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState([]);
  const [singleAssetMode, setSingleAssetMode] = useState(false);
  const [presetLoading, setPresetLoading] = useState(!!presetAssetId);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [modalRemark, setModalRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  /* 从资产详情进入：只借当前资产，不拉全量列表 */
  useEffect(() => {
    if (!presetAssetId) {
      setSingleAssetMode(false);
      setPresetLoading(false);
      setSelected([]);
      return;
    }
    let cancelled = false;
    setPresetLoading(true);
    api
      .get(`/assets/${presetAssetId}`)
      .then((data) => {
        if (cancelled) return;
        setSelected([
          {
            id: data.id,
            asset_name: data.asset_name,
            location_name: data.location_name || '',
          },
        ]);
        setSingleAssetMode(true);
      })
      .catch(() => {
        if (cancelled) return;
        message.error('无法加载该资产');
        setSingleAssetMode(false);
        setSelected([]);
      })
      .finally(() => {
        if (!cancelled) setPresetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [presetAssetId]);

  const fetchAssets = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 20 };
    if (keyword) params.keyword = keyword;
    api.get('/assets', { params })
      .then(res => {
        const items = res?.items || [];
        if (page === 1) {
          setAssets(items);
        } else {
          setAssets(prev => [...prev, ...items]);
        }
        setHasMore(items.length === 20);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, keyword]);

  useEffect(() => {
    if (singleAssetMode || presetLoading) return;
    fetchAssets();
  }, [singleAssetMode, presetLoading, fetchAssets]);

  const toggleSelect = (asset) => {
    setSelected(prev =>
      prev.find(a => a.id === asset.id)
        ? prev.filter(a => a.id !== asset.id)
        : [...prev, asset]
    );
  };

  const removeSelected = (id) => {
    setSelected(prev => prev.filter(a => a.id !== id));
  };

  const openRemarkModal = () => {
    if (selected.length === 0) {
      message.warning('请选择资产');
      return;
    }
    setModalRemark('');
    setRemarkModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!modalRemark.trim()) {
      message.warning('请填写借用备注');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/applies', {
        apply_type: 1,
        asset_ids: selected.map(a => a.id),
        remark: modalRemark.trim(),
      });
      if (res?.direct_done) {
        message.success('借用办理成功（无需审核）');
        setRemarkModalOpen(false);
        setModalRemark('');
        navigate(-1);
        return;
      }
      setRemarkModalOpen(false);
      setModalRemark('');
      setShowSuccess(true);
    } catch (err) {
      message.error(err.message || '提交失败');
      return;
    } finally {
      setSubmitting(false);
    }
  };

  const selectedIds = selected.map(a => a.id);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      /* 底部固定「申请借用」栏 + Layout 底栏留白，避免列表最后一项被挡住 */
      paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
    }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600 }}>申请借用</span>
      </div>

      {/* Search bar — 仅从首页等入口进入多选时使用 */}
      {!singleAssetMode && (
        <div style={{ padding: '12px 16px', background: '#fff' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="搜索资产/资产编号"
            allowClear
            value={keyword}
            onChange={e => { setKeyword(e.target.value); setPage(1); }}
            style={{ borderRadius: 20, background: '#f5f7fa' }}
          />
        </div>
      )}

      {presetAssetId && presetLoading && (
        <div style={{ padding: 80, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      )}

      {/* 单资产：从详情进入，展示当前借用对象 */}
      {singleAssetMode && selected.length > 0 && !presetLoading && (
        <div style={{ margin: '12px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 8 }}>借用资产</div>
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '14px 16px',
              border: '1px solid #e8f3ff',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{selected[0].asset_name}</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>{selected[0].location_name || '-'}</div>
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => navigate('/apply-borrow', { replace: true })}
            style={{ paddingLeft: 0, marginTop: 4, height: 'auto' }}
          >
            选择其他资产
          </Button>
        </div>
      )}

      {/* 已选资产：多选模式 */}
      {!singleAssetMode && selected.length > 0 && (
        <div
          style={{
            margin: '0 16px 8px',
            padding: '8px 10px',
            background: '#fff',
            borderRadius: 8,
            border: '1px solid #e8f3ff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px 8px',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', flexShrink: 0 }}>
            已选资产 <span style={{ color: '#1677ff' }}>共{selected.length}件</span>
          </span>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {selected.map((a) => (
              <Tag
                key={a.id}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  removeSelected(a.id);
                }}
                style={{
                  margin: 0,
                  maxWidth: '100%',
                  padding: '2px 6px',
                  fontSize: 12,
                  lineHeight: '20px',
                  borderRadius: 4,
                  background: '#e6f4ff',
                  borderColor: '#91caff',
                  color: '#0958d9',
                }}
                closeIcon={<CloseOutlined style={{ fontSize: 10 }} />}
              >
                <span
                  style={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    verticalAlign: 'bottom',
                  }}
                  title={a.asset_name}
                >
                  {a.asset_name}
                </span>
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* 可选资产列表 — 非详情入口 */}
      {!singleAssetMode && (
        <div style={{ padding: '12px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 8 }}>可选资产</div>
          <Spin spinning={loading && page === 1}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {assets.map(a => (
                <div
                  key={a.id}
                  onClick={() => toggleSelect(a)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: '#fff', borderRadius: 8, padding: '12px',
                    border: selectedIds.includes(a.id) ? '1px solid #1677ff' : '1px solid transparent',
                  }}
                >
                  <Checkbox checked={selectedIds.includes(a.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>{a.asset_name}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{a.location_name || '-'}</div>
                  </div>
                </div>
              ))}
            </div>
          </Spin>
          {hasMore && assets.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Button type="link" loading={loading && page > 1} onClick={() => setPage(p => p + 1)}>加载更多</Button>
            </div>
          )}
          {assets.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无可用资产</div>
          )}
        </div>
      )}

      {/* 固定在底栏上方（Layout 底栏高度 56px） */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 56,
          zIndex: 90,
          padding: '10px 16px',
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          background: '#fff',
          borderTop: '1px solid #eee',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
        }}
      >
        <Button
          type="primary"
          block
          size="large"
          onClick={openRemarkModal}
          disabled={selected.length === 0}
          style={{ borderRadius: 8, height: 46, fontSize: 16, fontWeight: 500 }}
        >
          申请借用
        </Button>
      </div>

      <Modal
        title="填写借用事由"
        open={remarkModalOpen}
        onCancel={() => {
          if (!submitting) setRemarkModalOpen(false);
        }}
        onOk={handleConfirmSubmit}
        okText="确认提交"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
        centered
      >
        <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
          借用备注 <span style={{ color: '#ff4d4f' }}>*</span>
        </div>
        <Input.TextArea
          rows={4}
          placeholder="请填写借用备注"
          value={modalRemark}
          onChange={(e) => setModalRemark(e.target.value)}
          maxLength={500}
          showCount
          style={{ borderRadius: 8, background: '#fafafa' }}
        />
      </Modal>

      {/* Success modal */}
      {showSuccess && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '40px 32px 32px', textAlign: 'center', width: 280 }}>
            <CheckCircleFilled style={{ fontSize: 56, color: '#52c41a', marginBottom: 16 }} />
            <div style={{ fontSize: 18, fontWeight: 600, color: '#333', marginBottom: 8 }}>提交成功</div>
            <div style={{ fontSize: 14, color: '#999', marginBottom: 24 }}>申请已提交，等待相关管理员审批</div>
            <Button
              type="primary"
              block
              size="large"
              style={{ borderRadius: 8 }}
              onClick={() => { setShowSuccess(false); navigate(-1); }}
            >
              我知道了
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
